import { useAuthState } from '@auth/hooks';
import { useSettingsDialog } from '@core/hooks/useSettingsDialog';
import { Menu, Badge, BadgeSize, MenuItem } from '@soteria/design-system-ve';
import { useCallback } from 'react';

import { headerContainer, userAvatar, userInfo, userName, systemAdminBadge } from './UserProfileMenu.css';

export interface UserProfileMenuProps {
  /**
   * Whether the menu is open
   */
  isOpen: boolean;

  /**
   * Callback when the menu is closed
   */
  onClose: () => void;

  /**
   * User data
   */
  user: {
    name: string;
    avatar?: string;
  };

  /**
   * Callback when the sign out button is clicked
   */
  onSignOut?: () => void;

  /**
   * ID of the element that triggered the menu
   */
  triggerId?: string;
}

/**
 * UserProfileMenu component that displays a menu with user profile options
 */
export function UserProfileMenu({ isOpen, onClose, user, onSignOut, triggerId }: UserProfileMenuProps) {
  // Use our custom hook to manage the settings dialog
  const { openDialog } = useSettingsDialog();

  // Check if the user is a system admin
  const { isUserSystemAdmin } = useAuthState();

  // Create a callback for the settings button
  const handleOpenSettings = useCallback(() => {
    openDialog();
    onClose();
  }, [openDialog, onClose]);

  // Handle sign out
  const handleSignOut = useCallback(() => {
    if (onSignOut) {
      onSignOut();
    }

    onClose();
  }, [onSignOut, onClose]);

  return (
    <Menu ariaLabel="User profile menu" isOpen={isOpen} placement="bottom end" triggerId={triggerId} onOpenChange={onClose}>
      {/* User profile header */}
      <div className={headerContainer}>
        <div className={userAvatar}>
          {user.avatar ? (
            <img alt={user.name} src={user.avatar} />
          ) : (
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                fill="currentColor"
              />
            </svg>
          )}
        </div>
        <div className={userInfo}>
          <div className={userName}>{user.name}</div>
          {isUserSystemAdmin && <Badge className={systemAdminBadge} size={BadgeSize.Small} text="System Admin" />}
        </div>
      </div>

      {/* Menu items */}
      <MenuItem
        icon={
          <svg fill="none" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19.14 12.94C19.18 12.64 19.2 12.33 19.2 12C19.2 11.68 19.18 11.36 19.13 11.06L21.16 9.48C21.34 9.34 21.39 9.07 21.28 8.87L19.36 5.55C19.24 5.33 18.99 5.26 18.77 5.33L16.38 6.29C15.88 5.91 15.35 5.59 14.76 5.35L14.4 2.81C14.36 2.57 14.16 2.4 13.92 2.4H10.08C9.84 2.4 9.65 2.57 9.61 2.81L9.25 5.35C8.66 5.59 8.12 5.92 7.63 6.29L5.24 5.33C5.02 5.25 4.77 5.33 4.65 5.55L2.74 8.87C2.62 9.08 2.66 9.34 2.86 9.48L4.89 11.06C4.84 11.36 4.8 11.69 4.8 12C4.8 12.31 4.82 12.64 4.87 12.94L2.84 14.52C2.66 14.66 2.61 14.93 2.72 15.13L4.64 18.45C4.76 18.67 5.01 18.74 5.23 18.67L7.62 17.71C8.12 18.09 8.65 18.41 9.24 18.65L9.6 21.19C9.65 21.43 9.84 21.6 10.08 21.6H13.92C14.16 21.6 14.36 21.43 14.39 21.19L14.75 18.65C15.34 18.41 15.88 18.09 16.37 17.71L18.76 18.67C18.98 18.75 19.23 18.67 19.35 18.45L21.27 15.13C21.39 14.91 21.34 14.66 21.15 14.52L19.14 12.94ZM12 15.6C10.02 15.6 8.4 13.98 8.4 12C8.4 10.02 10.02 8.4 12 8.4C13.98 8.4 15.6 10.02 15.6 12C15.6 13.98 13.98 15.6 12 15.6Z"
              fill="currentColor"
            />
          </svg>
        }
        label="Settings"
        onClick={handleOpenSettings}
      />
      <MenuItem
        showDivider
        icon={
          <svg fill="none" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
              fill="currentColor"
            />
          </svg>
        }
        label="Sign out"
        onClick={handleSignOut}
      />
    </Menu>
  );
}
